import { useEffect, useState } from 'react';
import { LocationType } from '../types/LocationType';
import calculateChannelDistance from '../helpers/calculateChannelDistance';
import { TENANT_TYPES } from 'constants-channel';
import { FeatureFlag } from 'constants-flags';
import { isBuildingChannel, isTenantLocationChannel } from '../helpers/channel';
import { byDistance } from '../helpers/sort';
import Storage, { StorageKeys } from '../helpers/Storage';
import { Channel } from '../types/ChannelType';
import useFlag from './useFlag';

function isSubTenantLocationChannel(channel: Channel) {
  return TENANT_TYPES.includes(channel.type) && channel.isSub && channel.parent;
}

function applyDistance(channel: Channel, location?: LocationType) {
  return {
    ...channel,
    distance: location
      ? calculateChannelDistance({
          ...location,
          channel,
        })
      : Number.MAX_SAFE_INTEGER,
  };
}

/**
 * Hook to find the best channel for the user, if they have nothing selected.
 * First checks storage for a previously selected channel, then falls back to selection logic.
 */
export function useBestDefaultChannel(
  channels: Channel[],
  location?: LocationType
) {
  const [bestDefaultChannel, setBestDefaultChannel] = useState<Channel | null>(
    null
  );
  const fallbackToFirstChannel = useFlag(
    FeatureFlag.ReturnFirstChannelIfDefaultChannelIsNull,
    false
  );

  async function getBestDefaultChannel() {
    // First check if there's a stored channel preference
    const primaryChannelId = await Storage.getItem(
      StorageKeys.PRIMARY_CHANNEL
    ).catch(() => null);

    if (primaryChannelId) {
      const storedChannel = channels.find(
        (channel: any) => channel._id === primaryChannelId
      );

      if (storedChannel) {
        return storedChannel;
      }
    }

    // Then try child buildings
    const buildings = channels
      .filter(isBuildingChannel)
      .map(channel => applyDistance(channel, location))
      .sort(byDistance);

    if (buildings.length > 0) {
      return buildings[0];
    }

    // Then try child companies
    const companies = channels
      .filter(isTenantLocationChannel)
      .map(channel => applyDistance(channel, location))
      .sort(byDistance);

    if (companies.length > 0) {
      return companies[0];
    }

    const subCompanies = channels
      .filter(isSubTenantLocationChannel)
      .map(channel => applyDistance(channel, location))
      .sort(byDistance);

    if (subCompanies.length > 0) {
      return subCompanies[0];
    }

    // Fallback: return first available channel when none match type-specific filters
    if (fallbackToFirstChannel && channels.length > 0) {
      return channels[0];
    }

    return null;
  }

  const channelIds = channels.map(channel => channel._id).join(',');

  useEffect(() => {
    getBestDefaultChannel().then(channel => {
      setBestDefaultChannel(channel);
    });
  }, [channelIds, location, fallbackToFirstChannel]);

  return bestDefaultChannel;
}
